import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

import backIcon from '../../assets/backspace-icon.png';
import menuIcon from '../../assets/adminmenu-icon.png';
import api from '../../api';

function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [allowedSections, setAllowedSections] = useState([]);

  const [badges, setBadges] = useState({
    disputes: 0,
    reports: 0,
    merchants: 0,
    appeals: 0,
    feedback: 0,
    sponsorships: 0,
    withdrawalsTestnet: 0,
    withdrawalsMainnet: 0,
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await api.get('/admin/access/me');
        if (!mounted) return;
        setAllowedSections(data.sections || []);
      } catch (e) {
        console.warn('Admin access load failed:', e?.response?.status || e?.message || e);
      }
    })();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/admin/metrics/attention');
        if (!mounted) return;

        setBadges({
          disputes: data.disputesPending ?? 0,
          reports: data.reportsSubmitted ?? 0,
          merchants: data.merchantsPending ?? 0,
          appeals: data.appealsSubmitted ?? 0,
          feedback: data.feedbackSubmitted ?? 0,
          sponsorships: data.sponsorshipPending ?? 0,
          withdrawalsTestnet: data.withdrawalsPendingTestnet ?? 0,
          withdrawalsMainnet: data.withdrawalsPendingMainnet ?? data.withdrawalsPending ?? 0,
        });
      } catch (e) {
        console.warn('Admin attention metrics unavailable:', e?.response?.status || e?.message || e);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const hasAccess = (section) => {
    return allowedSections.includes('*') || allowedSections.includes(section);
  };


  const tiles = [
    { key: 'disputes', label: 'Disputes', icon: '🛠', route: '/admin/disputes', section: 'disputes' },
    { key: 'reports', label: 'Reports', icon: '🚨', route: '/admin/reports', section: 'reports' },
    { key: 'merchants', label: 'Merchants', icon: '🧑‍💼', route: '/admin/merchants', section: 'merchants' },
    { key: 'users', label: 'Users', icon: '👥', route: '/admin/users', section: 'users' },
    { key: 'appeals', label: 'Appeals', icon: '✉️', route: '/admin/appeals', section: 'appeals' },


{ key: 'metrics', label: 'Metrics', icon: '📊', route: '/admin/metrics', section: 'metrics' },
{ key: 'graphs', label: 'Graphs', icon: '📈', route: '/admin/graphs', section: 'graphs' },

{
  key: 'revenue_analytics',
  label: 'Revenue Analytics',
  icon: '💹',
  route: '/admin/revenue-analytics',
  section: 'revenue_analytics',
},


{
  key: 'pioneer_summary',
  label: 'Pioneer Summary',
  icon: '🧭',
  route: '/admin/pioneer-summary',
  section: 'metrics',
},

{
  key: 'studio_analytics',
  label: 'Avatar Studio Analytics',
  icon: '🎨',
  route: '/admin/studio-analytics',
  section: 'metrics',
},

{ key: 'lang_country', label: 'Language & Country', icon: '🌍', route: '/admin/lang-country', section: 'lang_country' },


    { key: 'plans', label: 'Plans', icon: '💎', route: '/admin/plans', section: 'plans' },
    { key: 'feedback', label: 'Feedback', icon: '💬', route: '/admin/feedback', section: 'feedback' },
    { key: 'sponsorships', label: 'Sponsorships', icon: '🎯', route: '/admin/sponsorships', section: 'sponsorships' },
    { key: 'feedconfig', label: 'Feed Config', icon: '🧠', route: '/admin/feed-config', section: 'feedconfig' },

{
  key: 'survey',
  label: 'Experience Survey',
  icon: '📋',
  route: '/admin/survey',
  section: 'survey',
},

    { key: 'apps', label: 'Apps', icon: '📱', route: '/admin/apps', section: 'apps' },

    { key: 'withdrawals_test', label: 'Withdrawals (Testnet)', icon: '💸', route: '/admin/withdrawals?network=testnet', section: 'withdrawals' },
    { key: 'withdrawals_main', label: 'Withdrawals (Mainnet)', icon: '💸', route: '/admin/withdrawals?network=mainnet', section: 'withdrawals' },

    { key: 'wallet_test', label: 'Wallet (Testnet)', icon: '👛', route: '/admin/wallet?network=testnet', section: 'wallet' },
    { key: 'wallet_main', label: 'Wallet (Mainnet)', icon: '👛', route: '/admin/wallet?network=mainnet', section: 'wallet' },

    { key: 'active_wallets_main', label: 'Active Wallets (Mainnet)', icon: '🔐', route: '/admin/active-wallets', section: 'active_wallets_main' },
  ];


  if (!user || user.userType !== 'admin') {
    return <div className="admin-dashboard-denied">Access denied.</div>;
  }

  return (
    <div className="admin-dashboard-container">
      <div className="admin-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="admin-back-icon"
          onClick={() => navigate('/')}
        />
        <h2>Admin Dashboard</h2>

        <div className="admin-menu-wrapper">
          <img
            src={menuIcon}
            alt="Menu"
            className="admin-menu-icon"
            onClick={() => setShowMenu(!showMenu)}
          />
          {showMenu && (
            <div className="admin-dropdown">
              {hasAccess('test_payment') && (
                <div onClick={() => navigate('/admin/test-payment')}>
                  💰 Test Pi Payment
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="admin-scroll-area">
        <div className="admin-tiles-grid">
          {tiles.map((tile, i) => {
            const count =
              ({
                disputes: badges.disputes,
                reports: badges.reports,
                merchants: badges.merchants,
                appeals: badges.appeals,
                feedback: badges.feedback,
                sponsorships: badges.sponsorships,
                withdrawals_test: badges.withdrawalsTestnet,
                withdrawals_main: badges.withdrawalsMainnet,
              }[tile.key] ?? 0);

            const showDot = count > 0;
            const isAllowed = hasAccess(tile.section);

            return (
              <div
                key={tile.key || i}
                className={`admin-tile ${!isAllowed ? 'admin-tile-disabled' : ''}`}
                onClick={() => {
                  if (!isAllowed) return;
                  navigate(tile.route);
                }}
                aria-disabled={!isAllowed}
                title={!isAllowed ? 'You do not have access to this section' : tile.label}
              >
                {showDot && isAllowed && (
                  <span
                    className="admin-tile-dot"
                    aria-label={`${tile.label} has ${count} attention items`}
                    title={`${count} ${tile.label.toLowerCase()}`}
                  />
                )}

                <div className="admin-icon">{tile.icon}</div>
                <div className="admin-label">{tile.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
